'use client';

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiFetch } from '@/lib/api';
import { useAuth } from '@/hooks/use-auth';

export interface BudgetAlertConfig {
  id: number;
  period: string;
  threshold_type: string;
  threshold_value: number;
  limit_amount_usd: number;
  action_on_exceed: string;
  is_active: boolean;
  last_triggered_at: string | null;
  created_at: string;
  updated_at: string;
}

export interface BudgetStatus {
  period: string;
  current_spend_usd: number;
  limit_usd: number;
  percentage_used: number;
  is_exceeded: boolean;
  active_alerts: BudgetAlertConfig[];
}

export function useBudgetConfigs() {
  return useQuery<BudgetAlertConfig[]>({
    queryKey: ['admin', 'budget', 'configs'],
    queryFn: () => apiFetch<BudgetAlertConfig[]>('/budget/configs'),
  });
}

export function useCreateBudgetConfig() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: {
      period: string;
      threshold_type: string;
      threshold_value: number;
      limit_amount_usd: number;
      action_on_exceed: string;
    }) =>
      apiFetch('/budget/configs', {
        method: 'POST',
        body: JSON.stringify(data),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'budget'] });
    },
  });
}

export function useUpdateBudgetConfig() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      id,
      ...data
    }: {
      id: number;
      threshold_value?: number;
      limit_amount_usd?: number;
      action_on_exceed?: string;
      is_active?: boolean;
    }) =>
      apiFetch(`/budget/configs/${id}`, {
        method: 'PUT',
        body: JSON.stringify(data),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'budget'] });
    },
  });
}

export function useDeleteBudgetConfig() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: number) =>
      apiFetch(`/budget/configs/${id}`, { method: 'DELETE' }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'budget'] });
    },
  });
}

export function useBudgetStatus() {
  const { user } = useAuth();

  return useQuery<BudgetStatus[]>({
    queryKey: ['admin', 'budget', 'status'],
    queryFn: () => apiFetch<BudgetStatus[]>('/budget/status'),
    refetchInterval: 60_000,
    enabled: user?.role === 'admin',
  });
}
